import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SessionContext } from "../../types/sessionContext";
import { FormField } from "../../components/FormField";
import { getCurrentDemoConfig } from "../../config/demoSites"; // Removed StepConfig as it's not directly typed

interface Step2FormProps {
    currentStep: number; // This prop tells the component which step it is
    sessionContext: SessionContext;
    updateSessionContext: (newCtx: SessionContext) => void;
}

const Step2Form: React.FC<Step2FormProps> = ({ currentStep, sessionContext, updateSessionContext }) => {
    const { t } = useTranslation();
    // Ensure fields are accessed safely, especially if sessionContext might not be fully initialized
    const fields = sessionContext.layout.workflow.steps[currentStep]?.fields || {};
    const demoConfig = getCurrentDemoConfig();
    // Find the config for the *specific step this component is supposed to represent*
    const stepConfig = demoConfig.steps.find(step => step.id === currentStep && step.id === 2); // More specific for Step 2 logic

    const [isLoadingLLMData, setIsLoadingLLMData] = useState(false);
    const [llmDataError, setLLMDataError] = useState<string | null>(null);
    const [llmPopulatedFields, setLlmPopulatedFields] = useState<Record<string, boolean>>({});

    useEffect(() => {
        // Explicitly check if this effect is for Step 2 and the component is indeed representing Step 2
        if (currentStep !== 2 || !stepConfig || !sessionContext) {
            return;
        }

        const targetField = stepConfig.llmDataTargetField;

        // Proceed only if LLM_data is true, targetField is set,
        // and we haven't already marked this field as LLM-populated in this component instance.
        if (stepConfig.LLM_data && targetField && !llmPopulatedFields[targetField]) {
            const fetchLLMData = async () => {
                setIsLoadingLLMData(true);
                setLLMDataError(null);
                // IMPORTANT: Mark as "attempted" or "populated" immediately before the async call
                // This prevents re-fetch loops if the effect re-runs while waiting for the fetch
                // or if the fetched data is empty.
                setLlmPopulatedFields(prev => ({ ...prev, [targetField]: true }));

                console.log(`Fetching LLM data for Step 2, field ${targetField}, promptStep: ${stepConfig.promptStep}`);
                try {
                    const response = await fetch(`/api/get-llm-table-data?step_key=${stepConfig.promptStep}`);
                    if (!response.ok) {
                        const errorData = await response.json();
                        // If fetch fails, we've already marked it as "populated" to prevent loops.
                        // User would need to navigate away and back or have a manual retry to try again.
                        setLLMDataError(errorData.error || `Failed to fetch LLM data: ${response.statusText}`);
                        // Optionally, reset llmPopulatedFields[targetField] to false here if you want to allow auto-retry on next effect run,
                        // but be cautious of loops. For now, it stays true.
                        return; // Exit if response not ok
                    }
                    const result = await response.json();

                    if (result.data && result.targetField === targetField) {
                        console.log(`Received LLM data for ${targetField}:`, result.data);
                        const updatedSessionCtx = { ...sessionContext };
                        if (updatedSessionCtx.layout.workflow.steps[currentStep]?.fields) {
                            updatedSessionCtx.layout.workflow.steps[currentStep].fields[targetField] = result.data;
                            updateSessionContext(updatedSessionCtx);
                            // No need to setLlmPopulatedFields again, it was set before the fetch.
                        }
                    } else {
                        console.error("LLM data received but target field mismatch or no data.", result);
                        setLLMDataError("LLM data received but was not in the expected format or for the correct field.");
                    }
                } catch (error) {
                    console.error("Error fetching LLM table data:", error);
                    setLLMDataError(error instanceof Error ? error.message : "An unknown error occurred");
                    // As with response not ok, llmPopulatedFields[targetField] remains true.
                } finally {
                    setIsLoadingLLMData(false);
                }
            };

            // Check if the target field in the session context already has data.
            const currentTableValue = fields[targetField];
            if ((Array.isArray(currentTableValue) && currentTableValue.length === 0) || !currentTableValue) {
                // Data is empty, proceed to fetch.
                fetchLLMData();
            } else {
                // Data already exists (e.g., from user input, previous session, or already fetched).
                // Mark as LLM-populated to prevent fetching again.
                console.log(`Field ${targetField} (Step 2) already has data, skipping LLM fetch and marking as populated.`);
                setLlmPopulatedFields(prev => ({ ...prev, [targetField]: true }));
            }
        }
    }, [currentStep, stepConfig, sessionContext, updateSessionContext, llmPopulatedFields, fields]); // Dependencies

    const handleChange = (field: string, value: any) => {
        const updated = { ...sessionContext };
        if (updated.layout.workflow.steps[currentStep]?.fields) {
            updated.layout.workflow.steps[currentStep].fields[field] = value;
            updateSessionContext(updated);
            if (stepConfig?.llmDataTargetField === field) {
                setLlmPopulatedFields(prev => ({ ...prev, [field]: true }));
            }
        }
    };

    // This check is important: if this component is somehow rendered for a step it's not configured for.
    if (!stepConfig) {
        // This should ideally not happen if DemoLayout correctly renders Step2Form only for currentStep === 2
        console.warn(`Step2Form rendered for currentStep ${currentStep}, but its specific logic is for step 2.`);
        return <div>Configuration error or Step2Form rendered for an unintended step.</div>;
    }

    // Further safety: if this component is meant to be Step2Form, but stepConfig is for another step id.
    if (stepConfig.id !== 2) {
        console.warn(`Step2Form has a stepConfig for step ${stepConfig.id}, but expected 2.`);
        return <div>Step configuration mismatch.</div>;
    }

    return (
        <div>
            <h1 className="text-2xl font-semibold">{t(stepConfig.titleKey)}</h1>
            <p className="mb-4 text-gray-500">{t("base_demo.step2form.description")}</p>

            {isLoadingLLMData && <p className="text-blue-500">Loading evaluation data from AI...</p>}
            {llmDataError && <p className="text-red-500">Error loading AI data: {llmDataError}</p>}

            {stepConfig.fields.map(fieldConfig => (
                <FormField
                    key={fieldConfig.fieldName}
                    field={fieldConfig}
                    value={fields[fieldConfig.fieldName] || fieldConfig.defaultValue || ""}
                    onChange={value => handleChange(fieldConfig.fieldName, value)}
                    allFieldValues={fields}
                />
            ))}
        </div>
    );
};

export default Step2Form;
